import {clamp} from '@benchling/js-utils';
import {createStyles, Modal as StructureModal} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import React, {useMemo, useState} from 'react';

import {isNodeTesting} from 'lib/Browser';
import getTestProps from 'test/lib/cypress/getTestProps';
import ZoomControlStrip from 'ui-library/controls/ZoomControlStrip';

interface ChemicalStructureViewerProps {
  title: string;
  base64Svg: string;
  opened: boolean;
  onClose: () => void;
}

export const INITIAL_POSITION = {x: 0, y: 0};
export const DEFAULT_SCALE = 1;
export const MIN_SCALE = 0.1;
export const MAX_SCALE = 5;
export const ZOOM_BUTTON_INCREMENT = 0.1;
export const ZOOM_RESET_DELAY_MS = 200;
export const FLOAT_TOLERANCE = 0.001;

const useStyles = createStyles(() => ({
  modalBody: {
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  modalContent: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    width: '100%',
  },
}));

export default function ChemicalStructureViewer(props: ChemicalStructureViewerProps): JSX.Element {
  const {title, base64Svg, onClose, opened} = props;
  const [scale, setScale] = useState(DEFAULT_SCALE);
  const [position, setPosition] = useState(INITIAL_POSITION);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState(INITIAL_POSITION);

  const {classes} = useStyles();

  const handleWheel = (e: React.WheelEvent<HTMLDivElement>): void => {
    // normalize the delta for consistent behavior across mouse wheel (typically stepped values)
    // and touchpad (more "continuous" values)
    const delta = e.deltaY;
    const zoomIntensity = 0.001;

    setScale((prevScale) => {
      const newScale = prevScale - delta * zoomIntensity;
      return clamp(MIN_SCALE, MAX_SCALE, newScale);
    });
  };

  const adjustScale = (delta: number): void => {
    setScale((prevScale) => clamp(MIN_SCALE, MAX_SCALE, prevScale + delta));
  };

  const handleZoomInClick = (): void => {
    adjustScale(ZOOM_BUTTON_INCREMENT);
  };

  const handleZoomOutClick = (): void => {
    adjustScale(-ZOOM_BUTTON_INCREMENT);
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>): void => {
    e.preventDefault();
    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    });
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>): void => {
    if (!isDragging) {
      return;
    }
    e.preventDefault();
    setPosition({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y,
    });
  };

  const handleMouseUp = (): void => {
    setIsDragging(false);
  };

  const handleMouseLeave = (): void => {
    setIsDragging(false);
  };

  const handleReset = (): void => {
    setPosition(INITIAL_POSITION);
    setScale(DEFAULT_SCALE);
  };

  const handleClose = (): void => {
    onClose();
    // delay to avoid resetting the position and scale before the modal fades out
    setTimeout(() => {
      handleReset();
    }, ZOOM_RESET_DELAY_MS);
  };

  const isApproximatelyEqual = (value: number, comparison: number): boolean =>
    Math.abs(value - comparison) < FLOAT_TOLERANCE;

  const isCentered =
    isApproximatelyEqual(position.x, INITIAL_POSITION.x) &&
    isApproximatelyEqual(position.y, INITIAL_POSITION.y);
  const isDefaultScale = isApproximatelyEqual(scale, DEFAULT_SCALE);

  const zoomLevelLabel = useMemo(() => `${Math.round(scale * 100)}%`, [scale]);

  const isZoomInDisabled = scale >= MAX_SCALE - FLOAT_TOLERANCE;
  const isZoomOutDisabled = scale <= MIN_SCALE + FLOAT_TOLERANCE;
  const isAtDefaultState = isCentered && isDefaultScale;

  return (
    <StructureModal
      title={title}
      size="xl"
      radius={0}
      opened={opened}
      onClose={handleClose}
      classNames={{body: classes.modalBody, content: classes.modalContent}}
      // Legacy Enzyme test workaround. New components with RTL should omit withinPortal.
      withinPortal={!isNodeTesting}
    >
      <div
        className={css(styles.structureContainer, isDragging && styles.dragging)}
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
      >
        <img
          {...getTestProps({component: 'ChemicalStructureViewer', element: 'chemicalStructure'})}
          className={css(styles.fullScreenStructure)}
          src={`data:image/svg+xml;base64,${base64Svg}`}
          style={{
            transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
          }}
        />
        <div className={css(styles.zoomControlsOverlay)}>
          <ZoomControlStrip
            zoomLevelLabel={zoomLevelLabel}
            onZoomIn={handleZoomInClick}
            onZoomOut={handleZoomOutClick}
            onReset={handleReset}
            zoomInDisabled={isZoomInDisabled}
            zoomOutDisabled={isZoomOutDisabled}
            centerDisabled={isAtDefaultState}
          />
          <p className={css(styles.instructions)}>
            Shortcuts: Scroll to <strong>zoom</strong>. Click & drag to <strong>move</strong> the structure.
          </p>
        </div>
      </div>
    </StructureModal>
  );
}

const styles = StyleSheet.create({
  structureContainer: {
    display: 'flex',
    justifyContent: 'center',
    overflow: 'hidden',
    cursor: 'grab',
    position: 'relative',
  },
  dragging: {
    cursor: 'grabbing',
  },
  fullScreenStructure: {
    maxHeight: '100%',
    maxWidth: '100%',
    objectFit: 'contain',
    display: 'block',
    transformOrigin: 'center',
  },
  zoomControlsOverlay: {
    position: 'absolute',
    bottom: 0,
    left: 16,
    zIndex: 1,
  },
  instructions: {
    marginTop: '8px',
  },
});
